"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
// এই মডালগুলো বর্তমানে সাপ্লায়ারের জন্য, গ্রাহকের জন্য আলাদা মডাল প্রয়োজন হতে পারে
import AddSupplierModal from "../../components/HRM/AddSupplierModal"
import EditSupplierModal from "../../components/HRM/EditSupplierModal"
import AddSupplierGroupModal from "../../components/HRM/AddSupplierGroupModal"
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";

const CustomerManage = () => {
  const [customers, setCustomers] = useState([]) // নাম পরিবর্তন করা হয়েছে
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentCustomerId, setCurrentCustomerId] = useState(null) // নাম পরিবর্তন করা হয়েছে
  const [addCustomerGroupModalOpen, setAddCustomerGroupModalOpen] = useState(false) // নাম পরিবর্তন করা হয়েছে

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [customerToDeleteId, setCustomerToDeleteId] = useState(null) // নাম পরিবর্তন করা হয়েছে

  const { showSnackbar } = useOutletContext()

  const fetchCustomers = async () => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      // এখানে API এন্ডপয়েন্ট আপাতত সাপ্লায়ারের জন্য রাখা হয়েছে, যেমনটি অনুরোধ করা হয়েছে
      const response = await axios.get(`${BaseURL}/all/supplier`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      // ডেটা প্রপার্টি 'suppliers' থেকে 'customers' এ পরিবর্তন করা হয়েছে যদি API রেসপন্স ভিন্ন হয়
      const fetchedData = Array.isArray(response.data.suppliers) ? response.data.suppliers : []
      setCustomers(fetchedData)
      console.log("Customers (using supplier API) fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching customers (using supplier API):", err)
      setError("Failed to load customers. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedCustomers = [...customers].sort((a, b) => {
    // নাম পরিবর্তন করা হয়েছে
    if (sortConfig.key === null) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    if (sortConfig.key === "group") {
      aValue = a.group?.name || ""
      bValue = b.group?.name || ""
    }

    if (aValue < bValue) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  const filteredCustomers = sortedCustomers.filter((customer) => {
    // নাম পরিবর্তন করা হয়েছে
    const searchLower = searchTerm.toLowerCase()
    return (
      String(customer.name).toLowerCase().includes(searchLower) ||
      String(customer.mobile_no).toLowerCase().includes(searchLower) ||
      String(customer.address).toLowerCase().includes(searchLower) ||
      String(customer.group?.name || "")
        .toLowerCase()
        .includes(searchLower)
    )
  })

  const indexOfLastCustomer = currentPage * entriesPerPage // নাম পরিবর্তন করা হয়েছে
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage // নাম পরিবর্তন করা হয়েছে
  const displayedCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer) // নাম পরিবর্তন করা হয়েছে
  const totalPages = Math.ceil(filteredCustomers.length / entriesPerPage) // নাম পরিবর্তন করা হয়েছে

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredCustomers.length // নাম পরিবর্তন করা হয়েছে
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstCustomer + 1
  const endEntry = Math.min(indexOfLastCustomer, totalEntries)

  const handleDownloadPdf = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem("auth_token")
      // এখানে API এন্ডপয়েন্ট আপাতত সাপ্লায়ারের জন্য রাখা হয়েছে, যেমনটি অনুরোধ করা হয়েছে
      const response = await axios.get(`${BaseURL}/all/supplier/report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"]
      console.log("Content-Disposition Header:", contentDisposition)
      let filename = "customers_report.pdf" // ফাইলের নাম পরিবর্তন করা হয়েছে
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]
        } else {
          const unquotedFilenameMatch = contentDisposition.match(/filename=([^;]+)/)
          if (unquotedFilenameMatch && unquotedFilenameMatch[1]) {
            filename = unquotedFilenameMatch[1].trim()
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("PDF report downloaded successfully!", "success")
    } catch (err) {
      console.error("Error downloading PDF:", err)
      showSnackbar("Failed to download PDF report. Please try again.", "error")
    } finally {
      setDownloading(false)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleAddCustomerClick = () => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleCustomerAddedSuccess = (message) => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    showSnackbar(message, "success")
    fetchCustomers()
  }

  const handleEditCustomerClick = (customerId) => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setCurrentCustomerId(customerId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentCustomerId(null)
  }

  const handleCustomerUpdatedSuccess = (message) => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    showSnackbar(message, "success")
    fetchCustomers()
  }

  // Open confirmation dialog
  const handleDeleteCustomerClick = (customerId) => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setCustomerToDeleteId(customerId)
    setConfirmDialogOpen(true)
  }

  // Close confirmation dialog
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setCustomerToDeleteId(null)
  }

  // Execute delete after confirmation
  const handleConfirmCustomerDelete = async () => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    if (!customerToDeleteId) return

    setConfirmDialogOpen(false)
    console.log("Attempting to delete customer with ID:", customerToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      // এখানে API এন্ডপয়েন্ট আপাতত সাপ্লায়ারের জন্য রাখা হয়েছে, যেমনটি অনুরোধ করা হয়েছে
      const response = await axios.delete(`${BaseURL}/delete/supplier/${customerToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "Customer deleted successfully!", "success")
        console.log("Customer deleted successfully. Optimistically updating UI...")

        setCustomers((prevCustomers) => {
          // নাম পরিবর্তন করা হয়েছে
          const updatedCustomers = prevCustomers.filter((c) => {
            const isMatch = String(c.id) === String(customerToDeleteId)
            console.log(
              `Comparing c.id: ${c.id} (type: ${typeof c.id}) with customerToDeleteId: ${customerToDeleteId} (type: ${typeof customerToDeleteId}). Match: ${isMatch}`,
            )
            return !isMatch
          })
          console.log("Customers after optimistic update. New count:", updatedCustomers.length)
          return updatedCustomers
        })

        await fetchCustomers()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete customer.", "error")
        console.log("Failed to delete customer (API reported failure or unexpected response):", response.data.message)
      }
    } catch (err) {
      console.error("Error deleting customer:", err)
      showSnackbar("An error occurred while deleting the customer.", "error")
    } finally {
      setCustomerToDeleteId(null)
    }
  }

  const handleAddCustomerGroupClick = () => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setAddCustomerGroupModalOpen(true)
  }

  const handleAddCustomerGroupModalClose = () => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    setAddCustomerGroupModalOpen(false)
  }

  const handleCustomerGroupAddedSuccess = (message) => {
    // ফাংশনের নাম পরিবর্তন করা হয়েছে
    showSnackbar(message, "success")
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleAddCustomerClick}>
              Add Customer
            </Button>
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              All Customer
            </Button>
            <Button variant="contained" onClick={handleAddCustomerGroupClick}>
              Add Customer Group
            </Button>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="customer table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0" }}>
                        Sl
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("name")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Customer Name {getSortIcon("name")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("group")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Group {getSortIcon("group")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("mobile_no")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Mobile No {getSortIcon("mobile_no")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("address")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Address {getSortIcon("address")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedCustomers.length > 0 ? (
                      displayedCustomers.map((customer, index) => (
                        <TableRow key={customer.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstCustomer + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{customer.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{customer.group?.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{customer.mobile_no}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{customer.address}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditCustomerClick(customer.id)}
                              >
                                <Edit fontSize="small" />
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteCustomerClick(customer.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          No customers found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add Customer Modal (currently using AddSupplierModal) */}
      <AddSupplierModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handleCustomerAddedSuccess} />

      {/* Edit Customer Modal (currently using EditSupplierModal) */}
      {editModalOpen && (
        <EditSupplierModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleCustomerUpdatedSuccess}
          supplierId={currentCustomerId} // এখানে supplierId এর পরিবর্তে customerId ব্যবহার করা হচ্ছে
        />
      )}

      {/* Add Customer Group Modal (currently using AddSupplierGroupModal) */}
      <AddSupplierGroupModal
        open={addCustomerGroupModalOpen}
        onClose={handleAddCustomerGroupModalClose}
        onSaveSuccess={handleCustomerGroupAddedSuccess}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmCustomerDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default CustomerManage
